import React from 'react';
import type { AttentionItem } from '../../domain/types';
import type { AttentionItemWorkContextMembership } from '../../domain/radarThreads';
import {
  BrandIcon,
  Badge,
  WorkContextChip,
  ViewContextButton,
  ViewDetailButton,
  CardExpandToggle,
} from '../../ui/common';
import { jiraWorkflowTone } from './jiraCardVisuals';
import { collapseProjectChips, type JiraProjectChip } from '../../domain/projectCardIdentity';

interface JiraCardHeaderProps {
  item: AttentionItem;
  workflow: string;
  isExpanded: boolean;
  projectChips: JiraProjectChip[];
  workContext?: AttentionItemWorkContextMembership;
  onCardClick: () => void;
  onToggleExpanded: (e: React.MouseEvent) => void;
  onNavigateToJira: (key: string) => void;
  onNavigateToGitHub?: (id: string) => void;
  onNavigateToContext?: () => void;
  onOpenContext?: () => void;
  onOpenDetail?: () => void;
}

function MetaChip({
  label,
  testId,
  tone,
  onClick,
}: {
  label: string;
  testId?: string;
  tone: 'project-active' | 'project' | 'epic';
  onClick?: (e: React.MouseEvent) => void;
}): React.ReactElement {
  const styles =
    tone === 'epic'
      ? { background: 'var(--z-fill-2)', color: 'var(--z-muted-2)' }
      : tone === 'project-active'
        ? { background: 'var(--z-ok-bg, #ecfdf5)', color: 'var(--z-ok-ink, #065f46)' }
        : { background: 'var(--z-fill)', color: 'var(--z-muted-2)' };

  const commonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 999,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 10.5,
    fontWeight: 600,
    cursor: onClick ? 'pointer' : 'default',
    border: 'none',
    ...styles,
  };

  if (onClick) {
    return (
      <button type="button" data-testid={testId} onClick={onClick} style={commonStyle}>
        {label}
      </button>
    );
  }

  return (
    <span data-testid={testId} style={commonStyle}>
      {label}
    </span>
  );
}

export const JiraCardHeader: React.FC<JiraCardHeaderProps> = ({
  item,
  workflow,
  isExpanded,
  projectChips,
  workContext,
  onCardClick,
  onToggleExpanded,
  onNavigateToJira,
  onNavigateToGitHub,
  onNavigateToContext,
  onOpenContext,
  onOpenDetail,
}) => {
  const collapsed = collapseProjectChips(projectChips);
  const shown = isExpanded ? projectChips : collapsed.visible;
  const shownOverflow = isExpanded ? 0 : collapsed.overflow;
  const epicKey = item.parentEpicKey || item.rawJiraIssue?.fields.parentEpicKey;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCardClick();
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8, flexWrap: 'wrap' }}
      >
        <BrandIcon source="jira" size={15} />
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--z-muted-2)',
          }}
        >
          {item.ticket}
        </span>
        <Badge label={workflow} tone={jiraWorkflowTone(workflow)} />
        {workContext ? (
          <WorkContextChip membership={workContext} onNavigate={onNavigateToContext} />
        ) : null}
        {epicKey ? (
          <MetaChip
            testId="jira-epic-chip"
            label={`Epic · ${epicKey}`}
            tone="epic"
            onClick={(e) => {
              e.stopPropagation();
              onNavigateToJira(epicKey);
            }}
          />
        ) : null}
        {shown.map((chip) => (
          <MetaChip
            key={chip.name}
            testId={`jira-project-chip-${chip.name}`}
            label={chip.name}
            tone={chip.active ? 'project-active' : 'project'}
            onClick={
              chip.githubItemId && onNavigateToGitHub
                ? (e) => {
                    e.stopPropagation();
                    onNavigateToGitHub(chip.githubItemId!);
                  }
                : undefined
            }
          />
        ))}
        {shownOverflow > 0 ? (
          <MetaChip testId="jira-project-overflow" label={`+${shownOverflow}`} tone="project" />
        ) : null}
        {onOpenContext ? (
          <ViewContextButton
            onClick={(e) => {
              e.stopPropagation();
              onOpenContext();
            }}
          />
        ) : null}
        {onOpenDetail ? (
          <ViewDetailButton
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetail();
            }}
          />
        ) : null}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--z-dim)' }}>{item.age}</span>
        <CardExpandToggle
          isExpanded={isExpanded}
          onClick={onToggleExpanded}
          ariaLabel={isExpanded ? 'Contraer ticket' : 'Expandir ticket'}
        />
      </div>

      <h3
        style={{
          margin: '0 0 9px',
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1.4,
          letterSpacing: '-0.015em',
          color: 'var(--z-text-2)',
          textWrap: 'pretty',
        }}
      >
        {item.title}
      </h3>

      {!isExpanded ? (
        <div
          data-testid="jira-collapsed-ai"
          style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="var(--z-accent-3)"
            style={{ flex: '0 0 12px', marginTop: 3 }}
          >
            <path d="M12 2l1.9 6.1L20 10l-6.1 1.9L12 18l-1.9-6.1L4 10l6.1-1.9z" />
          </svg>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.55,
              color: 'var(--z-muted-3)',
              textWrap: 'pretty',
            }}
          >
            {item.ai}
          </p>
        </div>
      ) : null}
    </div>
  );
};
